import { assetUrl } from '../http/build-url';
import { asBoolean, asString, asStringArray } from '../http/normalise';
import { ApiEventBranding, ApiPortalTheme, ApiThemePalette } from './theme.dto';
import {
  EventBranding,
  PortalLoginBackground,
  PortalTheme,
  ThemeField,
  ThemePalette,
  ThemeStatus,
  ThemeTypography,
} from './theme.model';

const DEFAULT_PRIMARY = '#c5a572';
const DEFAULT_ACCENT = '#1a1a1a';

const THEME_FIELDS: ReadonlySet<string> = new Set<string>([
  'logo',
  'favicon',
  'displayName',
  'primary',
  'sidebar',
  'accent',
  'typography',
]);

function mapPalette(dto: ApiThemePalette | null | undefined): ThemePalette {
  return {
    primary: asString(dto?.primary) ?? DEFAULT_PRIMARY,
    secondary: asString(dto?.secondary),
    accent: asString(dto?.accent) ?? DEFAULT_ACCENT,
  };
}

function mapTypography(fontFamily: string | null | undefined): ThemeTypography {
  return { fontFamily: asString(fontFamily) };
}

function asThemeFields(value: unknown): ReadonlySet<ThemeField> {
  const fields = asStringArray(value).filter((field): field is ThemeField =>
    THEME_FIELDS.has(field)
  );
  return new Set<ThemeField>(fields);
}

function asThemeStatus(value: unknown): ThemeStatus {
  return value === 'applied' ? 'applied' : 'draft';
}

export function mapPortalTheme(
  dto: ApiPortalTheme,
  storageBase: string
): PortalTheme {
  return {
    clientId: asString(dto.clientId) ?? '',
    logoUrl: assetUrl(storageBase, dto.logoUrl),
    faviconUrl: assetUrl(storageBase, dto.faviconUrl),
    logoPath: asString(dto.logoUrl),
    faviconPath: asString(dto.faviconUrl),
    workspaceDisplayName: asString(dto.workspaceDisplayName),
    palette: mapPalette(dto.palette),
    typography: mapTypography(dto.fontFamily),
    sidebarColor: asString(dto.sidebarColor),
    editableByClient: asThemeFields(dto.editableByClient),
    applyToLoginScreen: asBoolean(dto.applyToLoginScreen, false),
    status: asThemeStatus(dto.status),
    loginBackground: mapLoginBackground(dto.loginBackground),
  };
}

/** Read so it can be written back untouched; `52c` is where it becomes editable. */
function mapLoginBackground(
  dto: ApiPortalTheme['loginBackground']
): PortalLoginBackground | null {
  if (dto === undefined || dto === null) {
    return null;
  }
  const fit = dto.fit;
  return {
    imageUrl: asString(dto.imageUrl),
    color: asString(dto.color),
    overlay: typeof dto.overlay === 'number' ? dto.overlay : null,
    fit: fit === 'cover' || fit === 'contain' || fit === 'tile' ? fit : null,
  };
}

export function mapEventBranding(
  dto: ApiEventBranding,
  storageBase: string
): EventBranding {
  return {
    eventId: asString(dto.eventId) ?? '',
    logoUrl: assetUrl(storageBase, dto.logoUrl),
    appIconUrl: assetUrl(storageBase, dto.appIconUrl),
    splashUrl: assetUrl(storageBase, dto.splashUrl),
    logoPath: asString(dto.logoUrl),
    appIconPath: asString(dto.appIconUrl),
    splashPath: asString(dto.splashUrl),
    palette: mapPalette(dto.palette),
    typography: mapTypography(dto.fontFamily),
  };
}
